// Bulk-select for the Berichtezentrale list. Mirrors task_bulk_select_controller.js,
// with one addition: "Alle N Treffer auswählen" sets a hidden select_all flag so the
// server re-resolves the whole filtered set instead of trusting a client-side id list.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "selectedCount",
    "normalHeader",
    "selectionHeader",
    "selectAllFlag",
    "selectAllBanner",
    "selectAllBannerText",
  ]

  static values = {
    total: Number, // total matches across all pages, not just this one
  }

  connect() {
    this.lastChecked = null
    this.update()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((cb) => {
      cb.checked = checked
    })
    this.resetSelectAllFlag()
    this.update()
  }

  toggle(event) {
    if (event.shiftKey && this.lastChecked) {
      const checkboxes = this.checkboxTargets
      const start = checkboxes.indexOf(this.lastChecked)
      const end = checkboxes.indexOf(event.target)
      const range = [start, end].sort((a, b) => a - b)

      for (let i = range[0]; i <= range[1]; i++) {
        checkboxes[i].checked = event.target.checked
      }
    }

    this.lastChecked = event.target
    this.resetSelectAllFlag()
    this.update()
  }

  // Hand the whole filtered result set to the server. The checkboxes stay checked
  // so the count still reads sensibly, but select_all=1 is what the controller acts on.
  selectAllMatching() {
    if (this.hasSelectAllFlagTarget) {
      this.selectAllFlagTarget.value = "1"
    }
    this.checkboxTargets.forEach((cb) => {
      cb.checked = true
    })
    this.update()
  }

  clear() {
    this.checkboxTargets.forEach((cb) => {
      cb.checked = false
    })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.resetSelectAllFlag()
    this.update()
  }

  resetSelectAllFlag() {
    if (this.hasSelectAllFlagTarget) {
      this.selectAllFlagTarget.value = "0"
    }
  }

  get selectedCount() {
    return this.checkboxTargets.filter((cb) => cb.checked).length
  }

  get selectAllActive() {
    return this.hasSelectAllFlagTarget && this.selectAllFlagTarget.value === "1"
  }

  update() {
    this.updateSelectAllState()
    this.updateHeaderVisibility()
    this.updateSelectAllBanner()
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const total = this.checkboxTargets.length
    const checked = this.selectedCount

    this.selectAllTarget.checked = checked === total && total > 0
    this.selectAllTarget.indeterminate = checked > 0 && checked < total
  }

  updateHeaderVisibility() {
    const hasSelection = this.selectedCount > 0

    if (this.hasNormalHeaderTarget && this.hasSelectionHeaderTarget) {
      this.normalHeaderTarget.classList.toggle("hidden", hasSelection)
      this.selectionHeaderTarget.classList.toggle("hidden", !hasSelection)
      this.selectionHeaderTarget.classList.toggle("flex", hasSelection)
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.selectAllActive
        ? this.totalValue
        : this.selectedCount
    }
  }

  // Offer "alle Treffer" only when the page really is a subset of the result set
  // and the whole page is already checked — otherwise the banner is noise.
  updateSelectAllBanner() {
    if (!this.hasSelectAllBannerTarget) return

    const pageSize = this.checkboxTargets.length
    const wholePageChecked = pageSize > 0 && this.selectedCount === pageSize
    const moreThanOnePage = this.totalValue > pageSize
    const show = wholePageChecked && moreThanOnePage && !this.selectAllActive

    this.selectAllBannerTarget.classList.toggle("hidden", !show)

    if (show && this.hasSelectAllBannerTextTarget) {
      this.selectAllBannerTextTarget.textContent = this.selectAllBannerTextTarget.dataset.template
        ? this.selectAllBannerTextTarget.dataset.template.replace("%{count}", pageSize)
        : ""
    }
  }
}
